import { Category2, Home2, Shop, ShoppingCart, User } from "iconsax-reactjs";
import type React from "react";

/** Translation keys under the `nav` namespace. */
export type NavLabelKey = "home" | "expressBuy" | "cart" | "account" | "more";

export interface NavDestination {
  href: string;
  icon: React.ComponentType<{
    variant: "Bold" | "Linear";
    size?: number | string;
    className?: string;
  }>;
  labelKey: NavLabelKey;
}

/**
 * The primary navigation destinations, shared by the mobile BottomNav and the
 * desktop TopNav links so both stay in sync. Order = display order.
 */
export const NAV_DESTINATIONS: NavDestination[] = [
  { href: "/", icon: Home2, labelKey: "home" },
  { href: "/fast-checkout", icon: Shop, labelKey: "expressBuy" },
  { href: "/shopping-cart", icon: ShoppingCart, labelKey: "cart" },
  { href: "/profile", icon: User, labelKey: "account" },
  { href: "/info", icon: Category2, labelKey: "more" },
];

/**
 * Extra routes that should activate a given destination (a destination is also
 * active when the current path matches one of its related prefixes).
 */
export const NAV_RELATED_PATHS: Record<string, string[]> = {
  "/": ["/shops", "/search"],
  "/shopping-cart": ["/checkout"],
  "/profile": ["/profile"],
  "/info": ["/about-us", "/stake", "/info/dao", "/info/investment"],
};

const stripTrailingSlash = (p: string): string => p.replace(/\/$/, "");

/**
 * Whether `href` is the active destination for `currentPath`. Matches the
 * destination itself (exact or as a path prefix) plus any related prefixes.
 */
export function isNavDestinationActive(
  currentPath: string | null | undefined,
  href: string,
): boolean {
  if (!currentPath) return false;

  const normalizedCurrent = stripTrailingSlash(currentPath);
  const normalizedHref = stripTrailingSlash(href);

  // Related-path match takes precedence over the root special-case below.
  const related = NAV_RELATED_PATHS[href];
  if (
    related?.some(
      (p) => normalizedCurrent === p || normalizedCurrent.startsWith(`${p}/`),
    )
  ) {
    return true;
  }

  if (normalizedHref === "" || normalizedHref === "/") {
    return normalizedCurrent === "" || normalizedCurrent === "/";
  }

  return (
    normalizedCurrent === normalizedHref ||
    normalizedCurrent.startsWith(`${normalizedHref}/`)
  );
}
